import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Settings as SettingsIcon, 
  Calendar, 
  AlertCircle, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  Save,
  Copy,
  FlaskConical,
  Scan,
  Activity,
  Stethoscope,
  Trash2,
  AlertTriangle,
  RotateCcw,
  MessageCircle,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  Phone,
  Key,
  Bell,
  Download,
  Upload,
  Database,
  Shield,
  Image as ImageIcon,
  Cloud,
  CloudOff,
  Link,
  Unlink,
  ExternalLink,
  Building2
} from 'lucide-react';
import { formatDateBR } from '../utils/dateUtils';

export default function SettingsPage() {
  const { isAdminGeral, isMaster } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deadlineDate, setDeadlineDate] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Horários de marcação
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [horariosConfig, setHorariosConfig] = useState(null);
  const [horarios, setHorarios] = useState([]);
  const [savingHorarios, setSavingHorarios] = useState(false);

  // WhatsApp Config
  const [whatsappConfig, setWhatsappConfig] = useState({
    zenvia_token: '',
    sender_number: '',
    template_id: 'lembrete_consulta',
    enabled: false,
    send_hour: 18,
    days_before: 1
  });
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  // Backup
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirmText, setRestoreConfirmText] = useState('');

  // License Status
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loadingLicense, setLoadingLicense] = useState(true);
  const [cnpjInput, setCnpjInput] = useState('');
  const [savingCnpj, setSavingCnpj] = useState(false);

  // Client Logo
  const [logoInfo, setLogoInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Google Drive Backup
  const [driveStatus, setDriveStatus] = useState(null);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [driveFolderId, setDriveFolderId] = useState('');
  const [driveEnabled, setDriveEnabled] = useState(false);
  const [savingDriveConfig, setSavingDriveConfig] = useState(false);
  const [triggeringDriveBackup, setTriggeringDriveBackup] = useState(false);
  const [disconnectingDrive, setDisconnectingDrive] = useState(false);
  const [driveClientId, setDriveClientId] = useState('');
  const [driveClientSecret, setDriveClientSecret] = useState('');
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [backupHour, setBackupHour] = useState(2);
  const [backupMinute, setBackupMinute] = useState(30);
  const [backupHour2, setBackupHour2] = useState(14);
  const [backupMinute2, setBackupMinute2] = useState(30);

  // Horário de Funcionamento
  const [operatingHours, setOperatingHours] = useState(null);
  const [opHoraInicio, setOpHoraInicio] = useState('07:00');
  const [opHoraFim, setOpHoraFim] = useState('18:00');
  const [opDias, setOpDias] = useState([0, 1, 2, 3, 4]);
  const [savingOperatingHours, setSavingOperatingHours] = useState(false);

  // Feriados
  const [holidays, setHolidays] = useState([]);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [loadingHolidays, setLoadingHolidays] = useState(false);

  // Endereços dos Locais de Atendimento
  const [locationConfig, setLocationConfig] = useState({
    hospital_nome: '',
    hospital_endereco: '',
    lab_municipal_nome: '',
    lab_municipal_endereco: ''
  });
  const [savingLocations, setSavingLocations] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchWhatsappConfig();
    fetchLicenseStatus();
    fetchLogoInfo();
    fetchLocations();
    if (isMaster()) {
      fetchOperatingHours();
      fetchHolidays();
      fetchDriveStatus();
    }
  }, []);

  useEffect(() => {
    fetchHorariosConfig();
  }, [currentMonth]);

  // Handle Google Drive OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      toast.success('Google Drive conectado com sucesso!');
      fetchDriveStatus();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/system-config');
      setConfig(response.data);
      if (response.data.deadline_date) {
        setDeadlineDate(response.data.deadline_date.split('T')[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const fetchHorariosConfig = async () => {
    try {
      const mes = currentMonth.getMonth() + 1;
      const ano = currentMonth.getFullYear();
      const response = await api.get(`/horarios-marcacao?mes=${mes}&ano=${ano}`);
      setHorariosConfig(response.data);
      setHorarios(response.data.horarios || []);
    } catch (error) {
      console.error('Erro ao buscar horários:', error);
      // Carregar valores padrão
      setHorarios([
        { categoria: "LAB", descricao: "Exames Laboratoriais", horario_inicio: "07:00", horario_fim: "09:00", turno: "M" },
        { categoria: "RX_MANHA", descricao: "Raio-X (Manhã)", horario_inicio: "07:00", horario_fim: "11:00", turno: "M" },
        { categoria: "RX_TARDE", descricao: "Raio-X (Tarde)", horario_inicio: "13:00", horario_fim: "17:00", turno: "T" },
        { categoria: "USG_PELVICA_PROSTATA_ABDOMINAL", descricao: "USG Pélvica, Próstata, Abdominal e Vias Urinárias", horario_inicio: "08:00", horario_fim: "12:00", turno: "M" },
        { categoria: "USG_TRANSVAGINAL_OBSTETRICA", descricao: "USG Transvaginal e Obstétrica", horario_inicio: "08:00", horario_fim: "12:00", turno: "M" },
        { categoria: "ECG", descricao: "Eletrocardiograma", horario_inicio: "07:00", horario_fim: "11:00", turno: "M" },
      ]);
    }
  };

  const handleUpdateDeadline = async (e) => {
    e.preventDefault();
    
    try {
      const deadlineISO = new Date(deadlineDate + 'T23:59:59Z').toISOString();
      await api.put('/system-config/deadline', { deadline_date: deadlineISO });
      toast.success('Data limite atualizada com sucesso!');
      fetchConfig();
    } catch (error) {
      toast.error('Erro ao atualizar data limite');
    }
  };

  const handleClearDeadline = async () => {
    if (!window.confirm('Deseja remover a data limite?')) return;
    
    try {
      await api.put('/system-config/deadline', { deadline_date: null });
      toast.success('Data limite removida!');
      setDeadlineDate('');
      fetchConfig();
    } catch (error) {
      toast.error('Erro ao remover data limite');
    }
  };

  const handleHorarioChange = (index, field, value) => {
    const newHorarios = [...horarios];
    newHorarios[index] = { ...newHorarios[index], [field]: value };
    setHorarios(newHorarios);
  };

  const handleSaveHorarios = async () => {
    try {
      setSavingHorarios(true);
      const mes = currentMonth.getMonth() + 1;
      const ano = currentMonth.getFullYear();
      
      await api.put('/horarios-marcacao', {
        mes,
        ano,
        horarios
      });
      
      toast.success('Horários de marcação salvos com sucesso!');
      fetchHorariosConfig();
    } catch (error) {
      toast.error('Erro ao salvar horários de marcação');
    } finally {
      setSavingHorarios(false);
    }
  };

  const handleCopyToNextMonth = async () => {
    const mesOrigem = currentMonth.getMonth() + 1;
    const anoOrigem = currentMonth.getFullYear();
    
    let mesDestino = mesOrigem + 1;
    let anoDestino = anoOrigem;
    if (mesDestino > 12) {
      mesDestino = 1;
      anoDestino += 1;
    }
    
    const mesNome = getMonthName(new Date(anoDestino, mesDestino - 1));
    
    if (!window.confirm(`Deseja copiar os horários de ${getMonthName()} para ${mesNome}?`)) return;
    
    try {
      await api.post(`/horarios-marcacao/copiar?mes_origem=${mesOrigem}&ano_origem=${anoOrigem}&mes_destino=${mesDestino}&ano_destino=${anoDestino}`);
      toast.success(`Horários copiados para ${mesNome}!`);
    } catch (error) {
      toast.error('Erro ao copiar horários');
    }
  };

  const handleResetSystem = async () => {
    if (resetConfirmText !== 'RESETAR') {
      toast.error('Digite RESETAR para confirmar');
      return;
    }
    
    try {
      setResetting(true);
      const response = await api.post('/system/reset');
      
      toast.success('Sistema resetado com sucesso!');
      setResetDialogOpen(false);
      setResetConfirmText('');
      
      // Mostrar estatísticas
      const stats = response.data.stats;
      console.log('Dados removidos:', stats);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar sistema');
    } finally {
      setResetting(false);
    }
  };

  // ==================== LICENSE STATUS ====================
  const fetchLicenseStatus = async () => {
    try {
      setLoadingLicense(true);
      const response = await api.get('/license/status');
      setLicenseInfo(response.data);
    } catch (error) {
      console.error('Erro ao carregar status da licenca:', error);
    } finally {
      setLoadingLicense(false);
    }
  };

  const getLicenseStatusLabel = (status) => {
    const labels = {
      active: 'Ativa',
      demo: 'Demonstração',
      expired: 'Expirada',
      suspended: 'Suspensa',
      grace_period: 'Periodo de Carencia',
      pending_license: 'Aguardando Vinculação',
      not_activated: 'Nao Ativada'
    };
    return labels[status] || status;
  };

  const getLicenseStatusColor = (status) => {
    const colors = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      demo: 'bg-blue-100 text-blue-700 border-blue-200',
      expired: 'bg-red-100 text-red-700 border-red-200',
      suspended: 'bg-red-100 text-red-700 border-red-200',
      grace_period: 'bg-orange-100 text-orange-700 border-orange-200',
      pending_license: 'bg-amber-100 text-amber-700 border-amber-200',
      not_activated: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const formatCnpjInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0,2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8)}`;
    return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`;
  };

  const handleSetCnpj = async (e) => {
    e.preventDefault();
    if (!cnpjInput.trim()) {
      toast.error('Informe o CNPJ');
      return;
    }
    const digits = cnpjInput.replace(/\D/g, '');
    if (digits.length !== 14) {
      toast.error('CNPJ invalido. Deve conter 14 digitos.');
      return;
    }
    try {
      setSavingCnpj(true);
      const response = await api.post('/license/set-cnpj', { cnpj: cnpjInput });
      if (response.data.auto_activated) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
      setCnpjInput('');
      fetchLicenseStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar CNPJ');
    } finally {
      setSavingCnpj(false);
    }
  };

  // ==================== LOGO FUNCTIONS ====================
  const fetchLogoInfo = async () => {
    try {
      const response = await api.get('/settings/logo/info');
      setLogoInfo(response.data);
      if (response.data.has_logo) {
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}/api/settings/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.error('Erro ao carregar info do logo:', error);
    }
  };

  // ===== Horário de Funcionamento =====
  const fetchOperatingHours = async () => {
    try {
      const response = await api.get('/system-hours');
      setOperatingHours(response.data);
      setOpHoraInicio(response.data.hora_inicio || '07:00');
      setOpHoraFim(response.data.hora_fim || '18:00');
      setOpDias(response.data.dias_semana || [0, 1, 2, 3, 4]);
    } catch (error) {
      console.error('Erro ao carregar horário:', error);
    }
  };

  const saveOperatingHours = async () => {
    try {
      setSavingOperatingHours(true);
      await api.put('/system-hours', {
        hora_inicio: opHoraInicio,
        hora_fim: opHoraFim,
        dias_semana: opDias
      });
      toast.success('Horário de funcionamento atualizado!');
      fetchOperatingHours();
    } catch (error) {
      toast.error('Erro ao salvar horário');
    } finally {
      setSavingOperatingHours(false);
    }
  };

  const toggleDay = (day) => {
    setOpDias(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort());
  };

  // ===== Feriados =====
  const fetchHolidays = async () => {
    try {
      setLoadingHolidays(true);
      const response = await api.get('/holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Erro ao carregar feriados:', error);
    } finally {
      setLoadingHolidays(false);
    }
  };

  const addHoliday = async () => {
    if (!newHolidayDate || !newHolidayName.trim()) {
      toast.error('Preencha a data e o nome do feriado');
      return;
    }
    try {
      await api.post('/holidays', { data: newHolidayDate, nome: newHolidayName.trim() });
      toast.success('Feriado adicionado!');
      setNewHolidayDate('');
      setNewHolidayName('');
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar feriado');
    }
  };

  const deleteHoliday = async (id) => {
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Feriado removido!');
      fetchHolidays();
    } catch (error) {
      toast.error('Erro ao remover feriado');
    }
  };

  const populateDefaultHolidays = async () => {
    try {
      const res = await api.post('/holidays/populate-defaults');
      toast.success(res.data.message);
      fetchHolidays();
    } catch (error) {
      toast.error('Erro ao popular feriados');
    }  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/settings/locations');
      setLocationConfig({
        hospital_nome: response.data.hospital_nome || '',
        hospital_endereco: response.data.hospital_endereco || '',
        lab_municipal_nome: response.data.lab_municipal_nome || '',
        lab_municipal_endereco: response.data.lab_municipal_endereco || ''
      });
    } catch (error) {
      console.error('Erro ao carregar endereços dos locais:', error);
    }
  };

  const handleSaveLocations = async () => {
    try {
      setSavingLocations(true);
      await api.put('/settings/locations', locationConfig);
      toast.success('Endereços dos locais atualizados com sucesso!');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar endereços');
    } finally {
      setSavingLocations(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato não permitido. Use JPG, PNG, WebP ou SVG.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Maximo: 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo atualizado com sucesso!');
      fetchLogoInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Deseja remover o logo do cliente?')) return;
    try {
      await api.delete('/settings/logo');
      toast.success('Logo removido');
      setLogoPreview(null);
      setLogoInfo({ has_logo: false });
    } catch (error) {
      toast.error('Erro ao remover logo');
    }
  };

  // ==================== WHATSAPP FUNCTIONS ====================
  const fetchWhatsappConfig = async () => {
    try {
      const response = await api.get('/whatsapp/config');
      const data = response.data;
      setWhatsappConfig({
        zenvia_token: data.zenvia_token_masked || data.zenvia_token || '',
        sender_number: data.sender_number || '',
        template_id: data.template_id || 'lembrete_consulta',
        enabled: data.enabled || false,
        send_hour: data.send_hour ?? 18,
        days_before: data.days_before ?? 1
      });
    } catch (error) {
      console.error('Erro ao carregar config WhatsApp:', error);
    }
  };

  const handleSaveWhatsapp = async () => {
    try {
      setSavingWhatsapp(true);
      await api.put('/whatsapp/config', whatsappConfig);
      toast.success('Configuração do WhatsApp salva com sucesso!');
      fetchWhatsappConfig();
    } catch (error) {
      toast.error('Erro ao salvar configuração do WhatsApp');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionStatus(null);
      const response = await api.post('/whatsapp/test');
      setConnectionStatus(response.data);
    } catch (error) {
      setConnectionStatus({ success: false, message: error.response?.data?.detail || 'Erro na conexão' });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSendRemindersNow = async () => {
    if (!window.confirm('Deseja enviar os lembretes de amanhã agora?')) return;
    try {
      setSendingReminders(true);
      const response = await api.post('/whatsapp/send-reminders');
      const r = response.data;
      toast.success(`Lembretes enviados: ${r.sent || 0} sucesso, ${r.errors || 0} erros, ${r.skipped || 0} ignorados`);
      fetchWhatsappLogs();
    } catch (error) {
      toast.error('Erro ao enviar lembretes');
    } finally {
      setSendingReminders(false);
    }
  };

  const fetchWhatsappLogs = async () => {
    try {
      const response = await api.get('/whatsapp/logs?limit=20');
      setWhatsappLogs(response.data);
      setShowLogs(true);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    }
  };

  // ==================== GOOGLE DRIVE FUNCTIONS ====================
  const fetchDriveStatus = async () => {
    try {
      setLoadingDrive(true);
      const response = await api.get('/drive/status');
      setDriveStatus(response.data);
      setDriveFolderId(response.data.folder_id || '');
      setDriveEnabled(response.data.enabled || false);
      setBackupHour(response.data.backup_hour ?? 2);
      setBackupMinute(response.data.backup_minute ?? 30);
      setBackupHour2(response.data.backup_hour_2 ?? 14);
      setBackupMinute2(response.data.backup_minute_2 ?? 30);
      if (response.data.google_client_id) {
        setDriveClientId(response.data.google_client_id);
      }
    } catch (error) {
      console.log('Drive status not available');
    } finally {
      setLoadingDrive(false);
    }
  };

  const handleDriveConnect = async () => {
    try {
      const response = await api.get('/drive/connect');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao conectar Google Drive');
    }
  };

  const handleSaveDriveCredentials = async () => {
    if (!driveClientId.trim() || !driveClientSecret.trim()) {
      toast.error('Preencha o Client ID e o Client Secret');
      return;
    }
    try {
      setSavingCredentials(true);
      const response = await api.post('/drive/credentials', {
        google_client_id: driveClientId.trim(),
        google_client_secret: driveClientSecret.trim()
      });
      toast.success(response.data.message);
      setDriveClientSecret('');
      fetchDriveStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar credenciais');
    } finally {
      setSavingCredentials(false);
    }
  };

  const handleDriveDisconnect = async () => {
    try {
      setDisconnectingDrive(true);
      await api.post('/drive/disconnect');
      toast.success('Google Drive desconectado');
      fetchDriveStatus();
    } catch (error) {
      toast.error('Erro ao desconectar');
    } finally {
      setDisconnectingDrive(false);
    }
  };

  const handleSaveDriveConfig = async () => {
    try {
      setSavingDriveConfig(true);
      const response = await api.put(`/drive/config?folder_id=${encodeURIComponent(driveFolderId)}&enabled=${driveEnabled}&backup_hour=${backupHour}&backup_minute=${backupMinute}&backup_hour_2=${backupHour2}&backup_minute_2=${backupMinute2}`);
      toast.success(response.data.message);
      fetchDriveStatus();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSavingDriveConfig(false);
    }
  };

  const handleDriveBackupNow = async () => {
    try {
      setTriggeringDriveBackup(true);
      const response = await api.post('/drive/backup-now');
      toast.success(response.data.message);
      fetchDriveStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer backup no Drive');
    } finally {
      setTriggeringDriveBackup(false);
    }
  };

  // ==================== BACKUP FUNCTIONS ====================
  const handleExportBackup = async () => {
    try {
      setCreatingBackup(true);
      const response = await api.get('/backup/export', { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateStr = now.toISOString().slice(0,10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0,5).replace(':', '');
      link.setAttribute('download', `backup_consultrack_${dateStr}_${timeStr}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup gerado e baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (restoreConfirmText !== 'RESTAURAR') {
      toast.error('Digite RESTAURAR para confirmar');
      return;
    }
    if (!restoreFile) {
      toast.error('Selecione um arquivo de backup');
      return;
    }

    try {
      setRestoringBackup(true);
      const formData = new FormData();
      formData.append('file', restoreFile);
      
      const response = await api.post('/backup/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const r = response.data;
      toast.success(r.message);
      setRestoreDialogOpen(false);
      setRestoreFile(null);
      setRestoreConfirmText('');
      
      // Recarregar configs
      fetchConfig();
      fetchWhatsappConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao restaurar backup');
    } finally {
      setRestoringBackup(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getMonthName = (date = currentMonth) => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const getCategoriaIcon = (categoria) => {
    switch (categoria) {
      case 'LAB': return <FlaskConical className="h-5 w-5" />;
      case 'RX_MANHA':
      case 'RX_TARDE': return <Scan className="h-5 w-5" />;
      case 'USG_PELVICA_PROSTATA_ABDOMINAL':
      case 'USG_TRANSVAGINAL_OBSTETRICA': return <Activity className="h-5 w-5" />;
      case 'ECG': return <Stethoscope className="h-5 w-5" />;
      default: return <Clock className="h-5 w-5" />;
    }
  };

  const getCategoriaColor = (categoria) => {
    switch (categoria) {
      case 'LAB': return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'RX_MANHA': return 'bg-orange-50 border-orange-200 text-orange-700';
      case 'RX_TARDE': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'USG_PELVICA_PROSTATA_ABDOMINAL': return 'bg-sky-50 border-sky-200 text-sky-700';
      case 'USG_TRANSVAGINAL_OBSTETRICA': return 'bg-pink-50 border-pink-200 text-pink-700';
      case 'ECG': return 'bg-rose-50 border-rose-200 text-rose-700';
      default: return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  const isDeadlineActive = config?.deadline_date && new Date(config.deadline_date) > new Date();
  const isDeadlinePassed = config?.deadline_date && new Date(config.deadline_date) <= new Date();

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Configurações</h1>
        <p className="text-slate-600">Gerencie as configurações do sistema</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seção: Status da Licença */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2" data-testid="license-status-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Shield className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Status da Licenca</h2>
              <p className="text-slate-500 text-sm">Informações sobre a licença desta instalação</p>
            </div>
          </div>

          {loadingLicense ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Carregando...</span>
            </div>
          ) : licenseInfo && licenseInfo.activated ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status */}
              <div className="p-4 rounded-xl border bg-slate-50" data-testid="license-status-card">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Status</p>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border ${getLicenseStatusColor(licenseInfo.status)}`} data-testid="license-status-badge">
                  {licenseInfo.status === 'active' && <CheckCircle className="h-4 w-4 mr-1.5" />}
                  {(licenseInfo.status === 'expired' || licenseInfo.status === 'suspended') && <XCircle className="h-4 w-4 mr-1.5" />}
                  {licenseInfo.status === 'grace_period' && <AlertTriangle className="h-4 w-4 mr-1.5" />}
                  {getLicenseStatusLabel(licenseInfo.status)}
                </span>
              </div>

              {/* Plano */}
              <div className="p-4 rounded-xl border bg-slate-50" data-testid="license-plan-card">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Plano</p>
                <p className="text-lg font-bold text-slate-900">{licenseInfo.plan_label || licenseInfo.plan}</p>
                {licenseInfo.client_name && (
                  <p className="text-sm text-slate-500 mt-1">{licenseInfo.client_name}</p>
                )}
              </div>

              {/* Validade */}
              <div className="p-4 rounded-xl border bg-slate-50" data-testid="license-expiry-card">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Validade</p>
                {licenseInfo.expiration_date ? (
                  <>
                    <p className="text-lg font-bold text-slate-900">
                      {formatDateBR(licenseInfo.expiration_date.split('T')[0])}
                    </p>
                    {licenseInfo.days_remaining !== undefined && licenseInfo.days_remaining >= 0 && (
                      <p className={`text-sm mt-1 ${licenseInfo.days_remaining <= 15 ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                        {licenseInfo.days_remaining} dia(s) restante(s)
                      </p>
                    )}
                    {licenseInfo.status === 'grace_period' && (
                      <p className="text-sm text-orange-600 font-medium mt-1">
                        Carencia: {licenseInfo.grace_days_remaining} dia(s)
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-lg font-bold text-slate-900">Sem prazo</p>
                )}
              </div>

              {/* CNPJ */}
              <div className="p-4 rounded-xl border bg-slate-50" data-testid="license-cnpj-card">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">CNPJ</p>
                <p className="text-lg font-bold text-slate-900">{licenseInfo.cnpj || '—'}</p>
              </div>

              {/* Limites (linha completa) */}
              <div className="md:col-span-2 lg:col-span-4 p-4 rounded-xl border bg-emerald-50 border-emerald-200">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-3">Limites do Plano</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-emerald-700">Usuários</p>
                    <p className="text-xl font-bold text-emerald-900">{licenseInfo.max_users ?? 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Unidades</p>
                    <p className="text-xl font-bold text-emerald-900">{licenseInfo.max_units ?? 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Pacientes</p>
                    <p className="text-xl font-bold text-emerald-900">{licenseInfo.max_patients ?? 'Ilimitado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Consultas/Mes</p>
                    <p className="text-xl font-bold text-emerald-900">{licenseInfo.max_appointments_month ?? 'Ilimitado'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : licenseInfo && licenseInfo.status === 'pending_license' ? (
            <div className="py-6" data-testid="license-pending">
              <div className="p-5 bg-amber-50 rounded-xl border border-amber-200 flex items-start gap-4 mb-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">CNPJ informado, aguardando vinculação</p>
                  <p className="text-sm text-amber-700 mt-1">
                    CNPJ: <strong>{licenseInfo.cnpj}</strong>
                  </p>
                  <p className="text-sm text-amber-600 mt-1">
                    O Master precisa criar/vincular uma licenca a este CNPJ para ativar o sistema.
                  </p>
                  {licenseInfo.set_by && (
                    <p className="text-xs text-amber-500 mt-2">
                      Informado por {licenseInfo.set_by}
                      {licenseInfo.set_at && ` em ${new Date(licenseInfo.set_at).toLocaleDateString('pt-BR')}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-6" data-testid="license-not-activated">
              <div className="text-center mb-6">
                <Shield className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">Nenhuma licença ativada nesta instalação</p>
                <p className="text-sm text-slate-400 mt-1">Informe o CNPJ da sua unidade para verificar se ja existe uma licenca</p>
              </div>
              {(isAdminGeral() || isMaster()) && (
                <form onSubmit={handleSetCnpj} className="max-w-md mx-auto">
                  <Label htmlFor="cnpj-input">CNPJ da Unidade/Cliente</Label>
                  <div className="flex gap-3 mt-2">
                    <Input
                      id="cnpj-input"
                      value={cnpjInput}
                      onChange={(e) => setCnpjInput(formatCnpjInput(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      className="h-11 rounded-lg font-mono"
                      maxLength={18}
                      data-testid="cnpj-input"
                    />
                    <Button
                      type="submit"
                      disabled={savingCnpj || cnpjInput.replace(/\D/g, '').length !== 14}
                      className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                      data-testid="cnpj-submit-button"
                    >
                      {savingCnpj ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      {savingCnpj ? 'Salvando...' : 'Informar CNPJ'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Se ja existir uma licenca para este CNPJ, o sistema sera ativado automaticamente.
                  </p>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Seção: Logo do Cliente */}
        {(isAdminGeral() || isMaster()) && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2" data-testid="client-logo-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-sky-100 rounded-xl">
                <ImageIcon className="h-6 w-6 text-sky-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Logo do Cliente</h2>
                <p className="text-slate-500 text-sm">Configure o logo exibido no cabeçalho do sistema</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Preview */}
              <div className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 min-h-[200px]">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo do Cliente"
                    className="max-h-32 max-w-full object-contain rounded-lg"
                    data-testid="logo-preview-image"
                  />
                ) : (
                  <div className="text-center">
                    <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Nenhum logo configurado</p>
                  </div>
                )}
                {logoInfo?.has_logo && logoInfo?.updated_at && (
                  <p className="text-xs text-slate-400 mt-3">
                    Atualizado em {new Date(logoInfo.updated_at).toLocaleDateString('pt-BR')}
                    {logoInfo.updated_by && ` por ${logoInfo.updated_by}`}
                  </p>
                )}
              </div>

              {/* Upload controls */}
              <div className="flex flex-col justify-center gap-4">
                <div>
                  <p className="text-sm text-slate-600 mb-3">
                    O logo será exibido no cabeçalho central do sistema para todos os usuários.
                    Formatos aceitos: JPG, PNG, WebP, SVG. Tamanho máximo: 5MB.
                  </p>
                </div>
                <div>
                  <Label htmlFor="logo-upload" className="cursor-pointer">
                    <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-sky-300 bg-sky-50 text-sky-700 font-medium hover:bg-sky-100 transition-colors ${uploadingLogo ? 'opacity-50 pointer-events-none' : ''}`}>
                      {uploadingLogo ? (
                        <><Loader2 className="h-5 w-5 animate-spin" /> Enviando...</>
                      ) : (
                        <><Upload className="h-5 w-5" /> {logoInfo?.has_logo ? 'Trocar Logo' : 'Enviar Logo'}</>
                      )}
                    </div>
                  </Label>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                    onChange={handleUploadLogo}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                </div>
                {logoInfo?.has_logo && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveLogo}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="logo-remove-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Remover Logo
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Coluna 1: Data Limite */}
        <div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-teal-50">
                <Calendar className="h-6 w-6 text-teal-600" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Data Limite para Marcações</h2>
                <p className="text-sm text-slate-600">Defina o prazo final para marcação de consultas</p>
              </div>
            </div>

            {isDeadlineActive && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-700">
                    Data limite ativa: {formatDateBR(config.deadline_date.split('T')[0])}
                  </p>
                  <p className="text-xs text-emerald-600">Marcações serão bloqueadas após esta data</p>
                </div>
              </div>
            )}

            {isDeadlinePassed && (
              <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-rose-600" />
                <div>
                  <p className="text-sm font-medium text-rose-700">
                    Data limite expirou em {formatDateBR(config.deadline_date.split('T')[0])}
                  </p>
                  <p className="text-xs text-rose-600">Novas marcações estão bloqueadas</p>
                </div>
              </div>
            )}

            <form onSubmit={handleUpdateDeadline} className="space-y-6">
              <div>
                <Label htmlFor="deadline">Nova Data Limite</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)}
                  className="h-11 rounded-lg"
                  required
                  data-testid="deadline-date-input"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Após esta data, apenas administradores poderão visualizar as marcações
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="submit" 
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="update-deadline-button"
                >
                  Atualizar Data Limite
                </Button>
                {config?.deadline_date && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleClearDeadline}
                    className="text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                  >
                    Remover Limite
                  </Button>
                )}
              </div>
            </form>
          </div>

          <div className="mt-6 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-start gap-3">
              <SettingsIcon className="h-5 w-5 text-slate-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-slate-900 mb-1">Sobre o Controle de Data Limite</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Este recurso permite definir um prazo para marcação de consultas. Após a data limite,
                  o sistema bloqueará automaticamente novas marcações e permitirá apenas visualização.
                  Administradores mantêm acesso total mesmo após o prazo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna 2: Endereços dos Locais de Atendimento */}
        <div>
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-indigo-50">
                  <Building2 className="h-6 w-6 text-indigo-600" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Endereços dos Locais</h2>
                  <p className="text-sm text-slate-600">Configure os endereços exibidos no cartão de confirmação</p>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              {/* Hospital */}
              <div className="p-4 rounded-lg border border-teal-200 bg-teal-50/40">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold text-teal-900">Hospital</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Nome do Local</Label>
                    <Input
                      value={locationConfig.hospital_nome}
                      onChange={(e) => setLocationConfig({ ...locationConfig, hospital_nome: e.target.value })}
                      placeholder="Hospital Eurídice Santana"
                      className="h-10 rounded-lg bg-white"
                      disabled={!isMaster()}
                      data-testid="location-hospital-nome"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Endereço</Label>
                    <Input
                      value={locationConfig.hospital_endereco}
                      onChange={(e) => setLocationConfig({ ...locationConfig, hospital_endereco: e.target.value })}
                      placeholder="Rua, número - Bairro, Cidade - UF, CEP"
                      className="h-10 rounded-lg bg-white"
                      disabled={!isMaster()}
                      data-testid="location-hospital-endereco"
                    />
                  </div>
                </div>
              </div>

              {/* Laboratório Municipal */}
              <div className="p-4 rounded-lg border border-orange-200 bg-orange-50/40">
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold text-orange-900">Laboratório Municipal</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Nome do Local</Label>
                    <Input
                      value={locationConfig.lab_municipal_nome}
                      onChange={(e) => setLocationConfig({ ...locationConfig, lab_municipal_nome: e.target.value })}
                      placeholder="Laboratório Municipal"
                      className="h-10 rounded-lg bg-white"
                      disabled={!isMaster()}
                      data-testid="location-lab-nome"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Endereço</Label>
                    <Input
                      value={locationConfig.lab_municipal_endereco}
                      onChange={(e) => setLocationConfig({ ...locationConfig, lab_municipal_endereco: e.target.value })}
                      placeholder="Travessa Osvaldo Fidelis S/N"
                      className="h-10 rounded-lg bg-white"
                      disabled={!isMaster()}
                      data-testid="location-lab-endereco"
                    />
                  </div>
                </div>
              </div>
            </div>

            {isMaster() && (
              <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
                <Button
                  onClick={handleSaveLocations}
                  disabled={savingLocations}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-locations-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingLocations ? 'Salvando...' : 'Salvar Endereços'}
                </Button>
              </div>
            )}
          </div>

          <div className="mt-6 p-6 bg-indigo-50 rounded-xl border border-indigo-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-indigo-900 mb-1">Sobre os Endereços dos Locais</h4>
                <p className="text-sm text-indigo-700 leading-relaxed">
                  Estes nomes e endereços aparecem no cartão de confirmação impresso entregue ao paciente.
                  O sistema escolhe automaticamente o endereço certo conforme a origem do agendamento
                  (Hospital ou Laboratório Municipal). Apenas o Master pode editar.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Seção: WhatsApp Notifications */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-green-100 rounded-xl">
              <MessageCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Notificações WhatsApp</h2>
              <p className="text-slate-500 text-sm">Configure o envio automático de lembretes via WhatsApp (Zenvia)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Configurações */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                <label className="flex items-center gap-3 cursor-pointer flex-1" data-testid="whatsapp-enabled-toggle">
                  <input
                    type="checkbox"
                    checked={whatsappConfig.enabled}
                    onChange={(e) => setWhatsappConfig({...whatsappConfig, enabled: e.target.checked})}
                    className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                  />
                  <div>
                    <span className="font-semibold text-slate-900">Ativar Notificações</span>
                    <p className="text-xs text-slate-500">Ative para habilitar o envio automático</p>
                  </div>
                </label>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${whatsappConfig.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                  {whatsappConfig.enabled ? 'Ativo' : 'Inativo'}
                </span>
              </div>

              <div>
                <Label className="flex items-center gap-2"><Key className="h-4 w-4 text-slate-400" /> Token da API Zenvia</Label>
                <Input
                  type="password"
                  value={whatsappConfig.zenvia_token}
                  onChange={(e) => setWhatsappConfig({...whatsappConfig, zenvia_token: e.target.value})}
                  placeholder="Insira o token da API Zenvia"
                  className="h-11 rounded-lg mt-1"
                  data-testid="whatsapp-token-input"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> Número Remetente (From)</Label>
                <Input
                  value={whatsappConfig.sender_number}
                  onChange={(e) => setWhatsappConfig({...whatsappConfig, sender_number: e.target.value})}
                  placeholder="Ex: 5511999998888"
                  className="h-11 rounded-lg mt-1"
                  data-testid="whatsapp-sender-input"
                />
                <p className="text-xs text-slate-500 mt-1">Número do WhatsApp Business registrado na Zenvia</p>
              </div>

              <div>
                <Label className="flex items-center gap-2"><Bell className="h-4 w-4 text-slate-400" /> Horário de Envio Diário</Label>
                <select
                  value={whatsappConfig.send_hour}
                  onChange={(e) => setWhatsappConfig({...whatsappConfig, send_hour: parseInt(e.target.value)})}
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 mt-1 text-sm"
                  data-testid="whatsapp-send-hour-select"
                >
                  {Array.from({length: 24}, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Horário em que os lembretes serão enviados automaticamente (Brasília)</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSaveWhatsapp}
                  disabled={savingWhatsapp}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="whatsapp-save-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {savingWhatsapp ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                  className="border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="whatsapp-test-button"
                >
                  {testingConnection ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Testar Conexão
                </Button>
              </div>

              {connectionStatus && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${connectionStatus.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
                  data-testid="whatsapp-connection-status"
                >
                  {connectionStatus.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {connectionStatus.message}
                </div>
              )}
            </div>

            {/* Painel de Ações e Info */}
            <div className="space-y-4">
              <div className="p-5 bg-green-50 rounded-xl border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" /> Como Funciona
                </h4>
                <ul className="text-sm text-green-800 space-y-2">
                  <li>1. Configure o token da API e o número remetente</li>
                  <li>2. Ative as notificações</li>
                  <li>3. Escolha o horário de envio (recomendado: 18h)</li>
                  <li>4. O sistema enviará lembretes automaticamente para pacientes com consulta no dia seguinte</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSendRemindersNow}
                  disabled={sendingReminders || !whatsappConfig.enabled}
                  variant="outline"
                  className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="whatsapp-send-now-button"
                >
                  {sendingReminders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Enviar Lembretes Agora
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchWhatsappLogs}
                  className="border-slate-300"
                  data-testid="whatsapp-logs-button"
                >
                  Ver Logs
                </Button>
              </div>

              {/* Logs */}
              {showLogs && (
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Últimos Envios</span>
                    <button onClick={() => setShowLogs(false)} className="text-slate-400 hover:text-slate-600 text-xs">Fechar</button>
                  </div>
                  {whatsappLogs.length === 0 ? (
                    <p className="p-4 text-sm text-slate-500 text-center">Nenhum log encontrado</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {whatsappLogs.map((log, i) => (
                        <div key={i} className="px-4 py-2 text-sm flex items-center justify-between">
                          <div>
                            <span className="font-medium text-slate-800">{log.patient_name}</span>
                            <span className="text-slate-400 ml-2">{log.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{log.sent_at}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {log.status === 'sent' ? 'Enviado' : 'Erro'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Seção: Backup Automático no Google Drive (Master apenas) */}
        {isMaster() && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2" data-testid="drive-backup-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Cloud className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Backup no Google Drive</h2>
              <p className="text-slate-500 text-sm">Backup automatico diario substituindo o anterior</p>
            </div>
          </div>

          {loadingDrive ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* Passo 1: Credenciais Google */}
              <div className="p-5 rounded-xl border border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-4">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold">1</span>
                  <h3 className="font-bold text-slate-800">Credenciais do Google Cloud</h3>
                  {driveStatus?.has_credentials && <CheckCircle className="h-4 w-4 text-emerald-600 ml-auto" />}
                </div>

                {/* Instruções colapsáveis */}
                <details className="mb-4">
                  <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium">
                    Como obter as credenciais?
                  </summary>
                  <div className="mt-3 p-4 bg-white border border-slate-200 rounded-lg">
                    <ol className="text-xs text-slate-600 space-y-2 list-decimal list-inside">
                      <li>Acesse <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                      <li>Crie um projeto novo (ou selecione um existente)</li>
                      <li>No menu lateral, va em <strong>"APIs e Servicos" &gt; "Biblioteca"</strong> e ative a <strong>Google Drive API</strong></li>
                      <li>Va em <strong>"APIs e Servicos" &gt; "Credenciais"</strong></li>
                      <li>Clique em <strong>"Criar Credenciais" &gt; "ID do Cliente OAuth 2.0"</strong></li>
                      <li>Selecione tipo <strong>"Aplicativo da Web"</strong></li>
                      <li>Em <strong>"URIs de redirecionamento autorizados"</strong>, adicione:
                        <code className="block mt-1 p-2 bg-slate-100 rounded text-xs select-all break-all">{driveStatus?.redirect_uri || `${window.location.origin}/api/drive/callback`}</code>
                      </li>
                      <li>Copie o <strong>Client ID</strong> e <strong>Client Secret</strong> e cole nos campos abaixo</li>
                    </ol>
                  </div>
                </details>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Client ID</Label>
                    <Input
                      value={driveClientId}
                      onChange={(e) => setDriveClientId(e.target.value)}
                      placeholder="xxxxxx.apps.googleusercontent.com"
                      className="mt-1 font-mono text-xs"
                      data-testid="drive-client-id-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Client Secret</Label>
                    <Input
                      type="password"
                      value={driveClientSecret}
                      onChange={(e) => setDriveClientSecret(e.target.value)}
                      placeholder={driveStatus?.has_credentials ? '(salvo - preencha para alterar)' : 'GOCSPX-xxxxxxxxx'}
                      className="mt-1 font-mono text-xs"
                      data-testid="drive-client-secret-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-600">URI de Redirecionamento (use no Google Cloud)</Label>
                    <div className="mt-1 p-2 bg-slate-100 rounded border border-slate-200 text-xs font-mono text-slate-700 select-all break-all">
                      {driveStatus?.redirect_uri || `${window.location.origin}/api/drive/callback`}
                    </div>
                  </div>
                  <Button
                    onClick={handleSaveDriveCredentials}
                    disabled={savingCredentials || (!driveClientId.trim())}
                    className="w-full bg-slate-800 hover:bg-slate-900"
                    data-testid="drive-save-credentials-btn"
                  >
                    {savingCredentials ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Key className="h-4 w-4 mr-2" />}
                    Salvar Credenciais
                  </Button>
                </div>
              </div>

              {/* Passo 2: Conectar Google Drive */}
              <div className={`p-5 rounded-xl border ${driveStatus?.has_credentials ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50/30 opacity-60 pointer-events-none'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className={`flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-bold ${driveStatus?.has_credentials ? 'bg-emerald-600' : 'bg-slate-400'}`}>2</span>
                  <h3 className="font-bold text-slate-800">Conectar ao Google Drive</h3>
                  {driveStatus?.connected && <CheckCircle className="h-4 w-4 text-emerald-600 ml-auto" />}
                </div>

                <div className={`p-4 rounded-xl border-2 ${driveStatus?.connected ? 'border-emerald-200 bg-emerald-50/30' : 'border-dashed border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {driveStatus?.connected ? (
                        <><Link className="h-5 w-5 text-emerald-600" /><div>
                          <p className="font-semibold text-emerald-800">Google Drive Conectado</p>
                          {driveStatus.connected_email && <p className="text-xs text-emerald-600">{driveStatus.connected_email}</p>}
                        </div></>
                      ) : (
                        <><CloudOff className="h-5 w-5 text-slate-400" /><p className="text-sm text-slate-500">Clique para autorizar o acesso ao Google Drive</p></>
                      )}
                    </div>
                    {driveStatus?.connected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDriveDisconnect}
                        disabled={disconnectingDrive}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="drive-disconnect-btn"
                      >
                        {disconnectingDrive ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Unlink className="h-4 w-4 mr-1" /> Desconectar</>}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleDriveConnect}
                        disabled={!driveStatus?.has_credentials}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="drive-connect-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" /> Conectar Google Drive
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Passo 3: Configurar Backup (só aparece quando conectado) */}
              {driveStatus?.connected && (
                <div className="p-5 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600 text-white text-xs font-bold">3</span>
                    <h3 className="font-bold text-slate-800">Configurar Backup</h3>
                  </div>

                  <div className="space-y-4">
                    {/* Pasta do Drive */}
                    <div>
                      <Label className="text-xs font-medium text-slate-600">ID da Pasta no Google Drive</Label>
                      <Input
                        value={driveFolderId}
                        onChange={(e) => setDriveFolderId(e.target.value)}
                        placeholder="Cole o ID da pasta do Google Drive"
                        className="mt-1"
                        data-testid="drive-folder-id-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Abra a pasta no Drive e copie o ID da URL: drive.google.com/drive/folders/<strong>[ESTE_ID]</strong>
                      </p>
                    </div>

                    {/* Toggle + Horário */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">Backup Automatico (2x ao dia)</p>
                          <p className="text-xs text-slate-500">Dois backups diarios para maior seguranca</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer" data-testid="drive-auto-toggle">
                        <input
                          type="checkbox"
                          checked={driveEnabled}
                          onChange={(e) => setDriveEnabled(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-300 peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                      </label>
                    </div>

                    {/* Seletores de horário - 2 backups por dia */}
                    <div className="space-y-3 px-1">
                      <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium text-slate-700 whitespace-nowrap w-28">1o Backup:</Label>
                        <div className="flex items-center gap-1">
                          <select
                            value={backupHour}
                            onChange={(e) => setBackupHour(parseInt(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                            data-testid="drive-backup-hour-select"
                          >
                            {Array.from({length: 24}, (_, i) => (
                              <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <span className="text-slate-500 font-bold">:</span>
                          <select
                            value={backupMinute}
                            onChange={(e) => setBackupMinute(parseInt(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                            data-testid="drive-backup-minute-select"
                          >
                            {[0, 15, 30, 45].map(m => (
                              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Label className="text-sm font-medium text-slate-700 whitespace-nowrap w-28">2o Backup:</Label>
                        <div className="flex items-center gap-1">
                          <select
                            value={backupHour2}
                            onChange={(e) => setBackupHour2(parseInt(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                            data-testid="drive-backup-hour-2-select"
                          >
                            {Array.from({length: 24}, (_, i) => (
                              <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <span className="text-slate-500 font-bold">:</span>
                          <select
                            value={backupMinute2}
                            onChange={(e) => setBackupMinute2(parseInt(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none"
                            data-testid="drive-backup-minute-2-select"
                          >
                            {[0, 15, 30, 45].map(m => (
                              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Botões */}
                    <div className="flex gap-3">
                      <Button
                        onClick={handleSaveDriveConfig}
                        disabled={savingDriveConfig}
                        className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                        data-testid="drive-save-config-btn"
                      >
                        {savingDriveConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Salvar Configuração
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleDriveBackupNow}
                        disabled={triggeringDriveBackup || !driveFolderId}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="drive-backup-now-btn"
                      >
                        {triggeringDriveBackup ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
                        Backup Agora
                      </Button>
                    </div>

                    {/* Status último backup */}
                    {driveStatus.last_backup_at && (
                      <div className={`p-4 rounded-xl border ${driveStatus.last_backup_status === 'success' ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {driveStatus.last_backup_status === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <p className={`text-sm font-semibold ${driveStatus.last_backup_status === 'success' ? 'text-emerald-800' : 'text-red-800'}`}>
                            Ultimo backup: {new Date(driveStatus.last_backup_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        <p className={`text-xs ${driveStatus.last_backup_status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {driveStatus.last_backup_message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* Seção: Backup do Sistema */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Database className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Backup do Sistema</h2>
              <p className="text-slate-500 text-sm">Exporte e restaure os dados do sistema</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Fazer Backup */}
            <div className="p-6 rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/30 flex flex-col items-center text-center gap-4">
              <div className="p-4 bg-indigo-100 rounded-full">
                <Download className="h-8 w-8 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Fazer Backup</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Gera um arquivo .json com todos os dados do sistema: pacientes, consultas, vagas, usuários, configurações e logs.
                </p>
              </div>
              <Button
                onClick={handleExportBackup}
                disabled={creatingBackup}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="backup-export-button"
              >
                {creatingBackup ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Gerando backup...</>
                ) : (
                  <><Download className="h-4 w-4 mr-2" /> Baixar Backup Completo</>
                )}
              </Button>
              <p className="text-xs text-slate-400">Salve o arquivo em local seguro (computador, pen drive, nuvem)</p>
            </div>

            {/* Restaurar Backup */}
            <div className="p-6 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/30 flex flex-col items-center text-center gap-4">
              <div className="p-4 bg-amber-100 rounded-full">
                <Upload className="h-8 w-8 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Restaurar Backup</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Restaura os dados a partir de um arquivo de backup gerado anteriormente. Os dados atuais serao substituidos.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setRestoreDialogOpen(true)}
                className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="backup-restore-button"
              >
                <Upload className="h-4 w-4 mr-2" /> Restaurar a partir de Backup
              </Button>
              <div className="flex items-center gap-2 text-xs text-amber-600">
                <AlertTriangle className="h-3 w-3" />
                <span>Esta ação substitui todos os dados atuais</span>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-indigo-500 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-600">
                <p className="font-semibold text-slate-800 mb-1">Recomendacoes de Seguranca</p>
                <ul className="space-y-1">
                  <li>- Faca backup regularmente (recomendado: semanalmente ou antes de mudancas importantes)</li>
                  <li>- Guarde o arquivo em local seguro e diferente do servidor</li>
                  <li>- Teste a restauração periodicamente para garantir que os backups estão funcionando</li>
                  <li>- O backup inclui: pacientes, consultas, vagas, usuários, procedimentos, configurações e logs</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Dialog: Restaurar Backup */}
        <Dialog open={restoreDialogOpen} onOpenChange={(open) => { setRestoreDialogOpen(open); if (!open) { setRestoreFile(null); setRestoreConfirmText(''); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" /> Restaurar Backup
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">
                  ATENÇÃO: Esta ação irá SUBSTITUIR todos os dados atuais do sistema pelos dados do backup. Esta ação não pode ser desfeita.
                </p>
              </div>

              <div>
                <Label>Selecione o arquivo de backup (.json)</Label>
                <Input
                  type="file"
                  accept=".json"
                  onChange={(e) => setRestoreFile(e.target.files[0])}
                  className="mt-1"
                  data-testid="backup-file-input"
                />
                {restoreFile && (
                  <p className="text-sm text-slate-500 mt-1">
                    Arquivo: {restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <div>
                <Label>Digite <span className="font-bold text-red-600">RESTAURAR</span> para confirmar</Label>
                <Input
                  value={restoreConfirmText}
                  onChange={(e) => setRestoreConfirmText(e.target.value.toUpperCase())}
                  placeholder="RESTAURAR"
                  className="mt-1"
                  data-testid="backup-confirm-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setRestoreDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRestoreBackup}
                  disabled={restoringBackup || restoreConfirmText !== 'RESTAURAR' || !restoreFile}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="backup-confirm-restore-button"
                >
                  {restoringBackup ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Restaurando...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Confirmar Restauração</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Seção: Horário de Funcionamento (Master Only) */}
        {isMaster() && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2" data-testid="operating-hours-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Horário de Funcionamento</h2>
                <p className="text-slate-500 text-sm">Configure os dias e horários em que o sistema estará disponível</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Hora de Início</Label>
                <Input
                  type="time"
                  value={opHoraInicio}
                  onChange={(e) => setOpHoraInicio(e.target.value)}
                  className="h-11"
                  data-testid="op-hora-inicio"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Hora de Término</Label>
                <Input
                  type="time"
                  value={opHoraFim}
                  onChange={(e) => setOpHoraFim(e.target.value)}
                  className="h-11"
                  data-testid="op-hora-fim"
                />
              </div>
            </div>

            <div className="mb-6">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">Dias de Funcionamento</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 0, label: 'Segunda' },
                  { value: 1, label: 'Terça' },
                  { value: 2, label: 'Quarta' },
                  { value: 3, label: 'Quinta' },
                  { value: 4, label: 'Sexta' },
                  { value: 5, label: 'Sábado' },
                  { value: 6, label: 'Domingo' }
                ].map(day => (
                  <button
                    key={day.value}
                    onClick={() => toggleDay(day.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      opDias.includes(day.value)
                        ? 'bg-teal-600 text-white'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                    data-testid={`day-toggle-${day.value}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                Fora do horário configurado, apenas <strong>Admin Geral</strong> e <strong>Master</strong> terão acesso ao sistema. Os demais usuários verão uma mensagem de sistema indisponível na tela de login.
              </p>
            </div>

            <Button
              onClick={saveOperatingHours}
              disabled={savingOperatingHours}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="save-operating-hours"
            >
              {savingOperatingHours ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</> : <><Save className="h-4 w-4 mr-2" /> Salvar Horário</>}
            </Button>
          </div>
        )}

        {/* Seção: Feriados (Master Only) */}
        {isMaster() && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:col-span-2" data-testid="holidays-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-orange-100 rounded-xl">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Feriados</h2>
                <p className="text-slate-500 text-sm">Gerencie os feriados nacionais e municipais em que o sistema ficará bloqueado</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Input
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                className="h-11 sm:w-48"
                data-testid="new-holiday-date"
              />
              <Input
                type="text"
                placeholder="Nome do feriado"
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                className="h-11 flex-1"
                data-testid="new-holiday-name"
              />
              <Button
                onClick={addHoliday}
                className="bg-teal-700 hover:bg-teal-800 h-11"
                data-testid="add-holiday-btn"
              >
                Adicionar
              </Button>
              <Button
                variant="outline"
                onClick={populateDefaultHolidays}
                className="h-11"
                data-testid="populate-holidays-btn"
              >
                Popular Padrão 2026
              </Button>
            </div>

            {loadingHolidays ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : holidays.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nenhum feriado cadastrado</p>
            ) : (
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Data</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Feriado</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {holidays.map(h => (
                      <tr key={h.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-700 font-mono">{formatDateBR(h.data)}</td>
                        <td className="px-4 py-3 text-slate-900">{h.nome}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => deleteHoliday(h.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            data-testid={`delete-holiday-${h.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Seção: Resetar Sistema */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-red-100 rounded-xl">
              <RotateCcw className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Resetar Sistema</h2>
              <p className="text-slate-500 text-sm">Limpar todos os dados para primeiro uso</p>
            </div>
          </div>

          <div className="p-6 bg-red-50 rounded-xl border border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-900 mb-2">ATENÇÃO: Ação Irreversível!</h4>
                <p className="text-sm text-red-700 leading-relaxed mb-3">
                  Esta ação irá <strong>remover permanentemente</strong> todos os dados do sistema:
                </p>
                <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                  <li>Todas as consultas e agendamentos</li>
                  <li>Todos os pacientes cadastrados</li>
                  <li>Todas as vagas do calendário</li>
                  <li>Todas as unidades de saúde</li>
                  <li>Todos os usuários (exceto você)</li>
                  <li>Todos os logs de auditoria</li>
                  <li>Todas as configurações de horários</li>
                </ul>
                <p className="text-sm text-red-800 font-semibold mt-3">
                  Esta ação NÃO pode ser desfeita!
                </p>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => setResetDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="reset-system-button"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Resetar Sistema
          </Button>
        </div>
      </div>

      {/* Modal de Confirmação de Reset */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Confirmar Reset do Sistema
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-800 text-sm">
                Você está prestes a <strong>apagar TODOS os dados</strong> do sistema.
                Esta ação é <strong>irreversível</strong>.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">
                Digite <strong className="text-red-600">RESETAR</strong> para confirmar:
              </Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                placeholder="Digite RESETAR"
                className="text-center font-mono text-lg"
                data-testid="reset-confirm-input"
              />
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setResetDialogOpen(false);
                  setResetConfirmText('');
                }}
              >
                Cancelar
              </Button>
              <Button 
                type="button" 
                onClick={handleResetSystem}
                disabled={resetConfirmText !== 'RESETAR' || resetting}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                data-testid="confirm-reset-button"
              >
                {resetting ? (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                    Resetando...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Confirmar Reset
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
